import { useState } from "react";
import moment from "moment-timezone";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  CallTranscriptionCall,
  CallTranscriptionSummary,
  useGetCallTranscriptionTranscript,
} from "@/lib/api/customer";
import { CommonDestinationTimezone } from "@/lib/utils";
import { CallContextLine, shouldShowCallCustomer } from "./CallContextLine";
import { TodoTranscriptContent } from "./TodoTranscript";

const GENERATED_WITH_WHISPER = "whisper-large-v3";
const GENERATED_WITH_NOVA_3 = "nova-3";

export function transcriptionLabel(transcription: CallTranscriptionSummary): string {
  if (transcription.generated_with === GENERATED_WITH_WHISPER) return "Rough Transcription";
  if (transcription.generated_with === GENERATED_WITH_NOVA_3) return "Upgraded Transcription";
  return transcription.generated_with || "Transcription";
}

/** "rough" or "upgraded", for sentences like "No rough transcription was produced". */
function transcriptionKind(transcription: CallTranscriptionSummary): string {
  return transcription.generated_with === GENERATED_WITH_NOVA_3 ? "upgraded" : "rough";
}

const FAILURE_LABELS: Record<string, string> = {
  TRANSCRIPTION_FAILED: "Transcription failed",
  UPGRADE_DECISION_FAILED: "Upgrade decision failed",
  TODO_EXTRACTION_FAILED: "Todo extraction failed",
  TODOS_PERSIST_FAILED: "Saving todos failed",
};

export function failureLabel(code: string): string {
  return FAILURE_LABELS[code] || code;
}

export function callDisplayTime(call: CallTranscriptionCall): string {
  return moment
    .tz(call.call_started_at || call.created_at, CommonDestinationTimezone)
    .format("MMM Do YYYY, h:mm A z");
}

interface CallTranscriptsDialogProps {
  call: CallTranscriptionCall | null;
  onClose: () => void;
}

export function CallTranscriptsDialog({ call, onClose }: CallTranscriptsDialogProps) {
  return (
    <Dialog
      open={call !== null}
      onOpenChange={(open: boolean) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="flex h-[calc(100vh-5vw)] w-[95vw] max-w-[95vw] flex-col sm:h-[calc(100vh-10vw)] sm:max-w-[90vw]">
        <DialogHeader>
          <DialogTitle>Call Transcripts</DialogTitle>
          {call && (
            <CallContextLine
              customerName={
                shouldShowCallCustomer(call.customer, call.from, call.to)
                  ? call.customer?.long_name || "Unknown Customer"
                  : null
              }
              callStartedAt={call.call_started_at || call.created_at}
              from={call.from}
              to={call.to}
            />
          )}
          <DialogDescription>The latest rough and upgraded transcription of this call.</DialogDescription>
        </DialogHeader>

        {/* Keyed on the call so reopening starts on the right tab. */}
        <CallTranscriptsTabs key={call?.id ?? "closed"} call={call} />

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CallTranscriptsTabs({ call }: { call: CallTranscriptionCall | null }) {
  // Upgraded first. The API sends rough first, so put the upgraded one in front.
  const isUpgraded = (transcription: CallTranscriptionSummary): boolean =>
    transcription.generated_with === GENERATED_WITH_NOVA_3;
  // Every re-run of a recording adds a new rough (and upgraded) transcription, so a call can carry several.
  // Show one tab per kind: the newest. On a tie the later one from the API wins.
  const newest = (candidates: CallTranscriptionSummary[]): CallTranscriptionSummary | undefined =>
    candidates.reduce<CallTranscriptionSummary | undefined>(
      (latest: CallTranscriptionSummary | undefined, candidate: CallTranscriptionSummary) =>
        !latest || moment(candidate.created_at).valueOf() >= moment(latest.created_at).valueOf() ? candidate : latest,
      undefined,
    );
  const callTranscriptions = call?.transcriptions ?? [];
  const upgraded = newest(callTranscriptions.filter(isUpgraded));
  const rough = newest(callTranscriptions.filter((transcription: CallTranscriptionSummary) => !isUpgraded(transcription)));
  const transcriptions = [upgraded, rough].filter(
    (transcription: CallTranscriptionSummary | undefined): transcription is CallTranscriptionSummary => !!transcription,
  );
  // A call that was never upgraded still gets the tab, disabled, so the layout is the same for every call.
  const showMissingUpgradedTab = !!call && !upgraded;
  const [activeTab, setActiveTab] = useState((upgraded ?? rough)?.id ?? "");

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="flex min-h-0 flex-1 flex-col">
      <TabsList className="shrink-0 self-start">
        {showMissingUpgradedTab && (
          <TabsTrigger
            value="upgraded-unavailable"
            disabled
            data-testid="call-transcript-tab-upgraded-unavailable"
            title="This call has no upgraded transcription"
          >
            Upgraded Transcription
          </TabsTrigger>
        )}
        {transcriptions.map((transcription: CallTranscriptionSummary) => (
          <TabsTrigger
            key={transcription.id}
            value={transcription.id}
            data-testid={`call-transcript-tab-${transcription.id}`}
          >
            {transcriptionLabel(transcription)}
          </TabsTrigger>
        ))}
      </TabsList>
      {transcriptions.map((transcription: CallTranscriptionSummary) => (
        <TabsContent
          key={transcription.id}
          value={transcription.id}
          className="flex min-h-0 flex-1 flex-col data-[state=inactive]:hidden"
        >
          <CallTranscriptTab
            transcription={transcription}
            // Offer the rough transcript from a failed upgrade, when there is one to show.
            fallback={
              isUpgraded(transcription) && rough?.has_transcript
                ? { label: transcriptionLabel(rough), onShow: () => setActiveTab(rough.id), testId: rough.id }
                : null
            }
          />
        </TabsContent>
      ))}
    </Tabs>
  );
}

// Mounted only while its tab is active, so each transcript loads on first view.
function CallTranscriptTab({
  transcription,
  fallback,
}: {
  transcription: CallTranscriptionSummary;
  fallback: { label: string; onShow: () => void; testId: string } | null;
}) {
  const transcriptQuery = useGetCallTranscriptionTranscript(transcription.id, transcription.has_transcript);
  const transcriptResult = transcriptQuery.data;
  const transcript = transcriptResult?.success ? transcriptResult.data.transcript : null;
  const transcriptError = transcriptQuery.isError
    ? "An unexpected error occurred while loading the transcript."
    : transcriptResult && !transcriptResult.success
      ? transcriptResult.message || "The transcript could not be loaded."
      : null;

  if (!transcription.has_transcript) {
    return (
      <div
        className="flex min-h-48 flex-col items-center justify-center gap-1 rounded-md border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive"
        data-testid={`call-transcript-failed-${transcription.id}`}
      >
        <p className="font-medium">No {transcriptionKind(transcription)} transcription was produced.</p>
        {transcription.failures.map((code: string) => (
          <p key={code}>{failureLabel(code)}</p>
        ))}
        {fallback && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={fallback.onShow}
            data-testid={`call-transcript-show-fallback-${fallback.testId}`}
          >
            Show {fallback.label}
          </Button>
        )}
      </div>
    );
  }

  return (
    <TodoTranscriptContent
      transcriptQuery={transcriptQuery}
      transcript={transcript}
      transcriptError={transcriptError}
    />
  );
}
